/* eslint-disable react-hooks/exhaustive-deps */
import { memo, useEffect, useState } from "react";
import ReactSelect from "react-select";
import makeAnimated from "react-select/animated";
import { allOption, Option, OptionWithoutChekbox, MultiValue, ValueContainer, SingleValue } from "./SelectWrapperComponents";
import { colourStyles } from "./SelectWrapperHelper";
import AsyncSelect from 'react-select/async';
import { compareByColumnAsc } from "../../workflows/util";
import "./SelectWrapper.scss";

const animatedComponents = makeAnimated(); 

interface SelectWrapperProps {
  id?: string,
  labelText?: string,
  options: any[],
  value: any,
  isLoading?: boolean,
  onChange?: (selected: any) => void,
  placeholder?: string,
  isMulti?: boolean,
  allowSelectAll?: boolean,
  menuShouldScrollIntoView?: boolean,
  closeMenuOnSelect?: boolean,
  hideSelectedOptions?: boolean,
  maxOptionsToShow?: number,
  allSelectedText?: string,
  className?: string,
  isSearchable?: boolean,
  valueKey?: string,
  labelKey?: string,
  isDisabled?: boolean,
  menuIsOpen?: boolean,
  captureMenuScroll?: boolean,
  isClearable?: boolean,
  isSorted?: boolean,
  isAsync?: boolean,
  asyncLoadOptions?: any,
  isAsyncCacheOptions?: boolean,
}

const getReactSelectOptions = (options: any[], allowSelectAll: boolean, isMulti: boolean, valueKey: string, labelKey: string) => {
  let reactSelectOptions = options;
  if (typeof options[0] === "string" || typeof options[0] === "number") {
    reactSelectOptions = options.map((item: any) => ({ [labelKey]: item, [valueKey]: item }));
  }
  if (options.length && allowSelectAll && isMulti) {
    const updatedAllOption = {
      [valueKey]: allOption.value,
      [labelKey]: allOption.label,
    };
    reactSelectOptions = reactSelectOptions.filter((item: any) => item.label !== "Select All");
    reactSelectOptions = [updatedAllOption, ...reactSelectOptions];
  }
  return reactSelectOptions;
};

const customFilterOptions = (option: any, inputValue: string) => {
  if (typeof option.label === "string") {
    return option.label.toLowerCase().includes(inputValue.toLowerCase());
  }
  return true;
};

const getReactSelectValue = (value: any, options: any[], allowSelectAll: boolean, isMulti: boolean, valueKey: string, labelKey: string) => {
  let reactSelectValue = value;
  if (Array.isArray(value) && (typeof value[0] === "string" || typeof value[0] === "number")) {
    reactSelectValue = value.map((item: any) => ({ [valueKey]: item, [labelKey]: item }));
  } else if (typeof value === "string" || typeof value === "number") {
    if (typeof options[0] === "object")
      reactSelectValue = options.find(option => option[valueKey] === value);
    else {
      const option = options.find(option => option === value);
      reactSelectValue = { label: option, value: option };
    }
  }
  if (isMulti && allowSelectAll && (value.length === options.length)) {
    const updatedAllOption = {
      [valueKey]: allOption.value,
      [labelKey]: allOption.label,
    };
    reactSelectValue = [updatedAllOption, ...options];
  }
  return reactSelectValue;
};

const SelectWrapper = (props: SelectWrapperProps) => {
  const { labelText, options, value, onChange = () => null, isMulti = false, allowSelectAll = false, menuShouldScrollIntoView = false, hideSelectedOptions = false, allSelectedText = "All Selected", className = "", isSearchable = true, valueKey = "value", labelKey = "label", isDisabled = false, captureMenuScroll = false, isClearable = false, maxOptionsToShow = 3, isSorted = false, isAsync = false, asyncLoadOptions, isAsyncCacheOptions = false, isLoading = false } = props;
  const [optionsData, setOptionsData] = useState(getReactSelectOptions(options, allowSelectAll, isMulti, valueKey, labelKey));

  useEffect(() => {
    setOptionsData(getReactSelectOptions(options, allowSelectAll, isMulti, valueKey, labelKey));
  }, [options]);


  const changeHandler = (text: string) => {
    if (isSorted) {
      const startsWithdata: any[] = [];
      const includesWithData: any[] = [];
      const remainingOptions: any[] = [];
      optionsData.forEach((item) => {
        const itemLabel = item.label.toLowerCase();
        const itemStartsWith = itemLabel.startsWith(text.toLowerCase());
        const itemIncludes = itemLabel.includes(text.toLowerCase());
        if (itemStartsWith) {
          startsWithdata.push(item);
        }
        else if (!itemStartsWith && itemIncludes) {
          includesWithData.push(item);
        }
        else {
          remainingOptions.push(item);
        }
      });
      startsWithdata.sort(compareByColumnAsc("label"));
      includesWithData.sort(compareByColumnAsc("label"));
      setOptionsData(getReactSelectOptions([...startsWithdata, ...includesWithData, ...remainingOptions], allowSelectAll, isMulti, valueKey, labelKey));
    }
  };



  const handleChangeWithSelectAll = (selected: any, event: any) => {
    if (selected !== null && selected.length > 0) {
      if (selected[selected.length - 1].value === allOption.value && event.action === "select-option") {
        return onChange([...props.options]);
      }

      let result = [];

      if (selected.length === options.length) {
        if (selected.find((option: any) => option.value === allOption.value)) {
          result = selected.filter(
            (option: any) => option.value !== allOption.value
          );
        } else if (event.action === "select-option") {
          result = [...options];
        }

        return onChange(result);
      }
    }
    return onChange(selected);
  };

  const updatedProps = {
    ...props,
    components: {
      Option: props.isMulti ? Option : OptionWithoutChekbox,
      MultiValue,
      ValueContainer,
      SingleValue,
      animatedComponents,
    },
    styles: colourStyles,
    menuShouldScrollIntoView,
    hideSelectedOptions,
    className: `select-wrapper-cont ${className}`,
    allSelectedText,
    value: getReactSelectValue(value, options, allowSelectAll, isMulti, valueKey, labelKey),
    isSearchable,
    getOptionLabel: (option: any) => option[labelKey],
    getOptionValue: (option: any) => option[valueKey],
    isDisabled,
    options: optionsData,
    onChange: isMulti && allowSelectAll ? handleChangeWithSelectAll : onChange,
    captureMenuScroll,
    isClearable,
    valueKey,
    labelKey,
    maxOptionsToShow
  };

  return (
    <>
      {!!labelText && <label className="cds--label">{props.labelText}</label>}
      {isAsync ?
        <AsyncSelect {...updatedProps} cacheOptions={isAsyncCacheOptions} defaultOptions={options} loadOptions={asyncLoadOptions} isLoading={isLoading} />
        :
        <ReactSelect {...updatedProps} filterOption={customFilterOptions} onInputChange={changeHandler} isLoading={isLoading} />
      }
    </>
  );
};

export default memo(SelectWrapper);
